import { useOutletContext, Link } from "react-router-dom";
import { PlayCircle, Clock, FileText, Trash2 } from "lucide-react";
import { useEccHubStats, useBulkDeleteRuns } from "@/hooks/useEcc";
import { useAuth } from "@/hooks/useAuth";
import type { EccArea } from "@/api/types";

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function statusLabel(s: string | null) {
  if (!s) return "—";
  const map: Record<string, string> = {
    done: "Completado",
    failed: "Fallido",
    pending: "Pendiente",
    transcribing: "Transcribiendo",
    analyzing: "Analizando",
    generating: "Generando",
    downloading: "Descargando",
    fetching_cdr: "Obteniendo CDR",
  };
  return map[s] ?? s;
}

export function EccHubPage() {
  const { area } = useOutletContext<{ area: EccArea }>();
  const { data, isPending } = useEccHubStats(area);
  const { user } = useAuth();
  const bulkDelete = useBulkDeleteRuns();

  function handleBulkDelete() {
    if (!window.confirm("¿Borrar TODAS las corridas fallidas y pendientes de todas las áreas? Esta acción no se puede deshacer.")) return;
    bulkDelete.mutate(undefined, {
      onSuccess: (res) => window.alert(`Se eliminaron ${res.deleted} corrida(s).`),
    });
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard
          label="Corridas últimos 7 días"
          value={isPending ? "…" : (data?.runs_last_7d ?? 0)}
        />
        <StatCard
          label="SRPs oficiales"
          value={isPending ? "…" : (data?.official_srps ?? 0)}
        />
        <StatCard
          label="Última corrida"
          value={isPending ? "…" : fmtDate(data?.last_run_at ?? null)}
          sub={data ? statusLabel(data.last_run_status) : undefined}
        />
        <StatCard
          label="Pendientes de aprobación"
          value={isPending ? "…" : (data?.pending_approvals ?? 0)}
        />
      </div>

      {!isPending && !data && (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          Sin datos para el área seleccionada. El backend ECC aún no está activo.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Link
          to={`/ecc/new?area=${area}`}
          className="flex items-center gap-3 rounded-lg border bg-card p-4 transition-colors hover:bg-accent"
        >
          <PlayCircle className="size-5 text-primary" />
          <div>
            <div className="text-sm font-medium">Nuevo análisis</div>
            <div className="text-xs text-muted-foreground">
              Selecciona periodo y corre un SRP
            </div>
          </div>
        </Link>
        <Link
          to={`/ecc/history?area=${area}`}
          className="flex items-center gap-3 rounded-lg border bg-card p-4 transition-colors hover:bg-accent"
        >
          <Clock className="size-5 text-muted-foreground" />
          <div>
            <div className="text-sm font-medium">Historial</div>
            <div className="text-xs text-muted-foreground">
              Ver corridas anteriores
            </div>
          </div>
        </Link>
        <Link
          to={`/ecc/catalog?area=${area}`}
          className="flex items-center gap-3 rounded-lg border bg-card p-4 transition-colors hover:bg-accent"
        >
          <FileText className="size-5 text-muted-foreground" />
          <div>
            <div className="text-sm font-medium">Catálogo de SRPs</div>
            <div className="text-xs text-muted-foreground">
              Reportes oficiales del área
            </div>
          </div>
        </Link>
      </div>

      {user?.role === "admin" && (
        <div className="flex justify-end border-t pt-4">
          <button
            type="button"
            onClick={handleBulkDelete}
            disabled={bulkDelete.isPending}
            className="flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
          >
            <Trash2 className="size-3.5" />
            {bulkDelete.isPending ? "Limpiando…" : "Limpiar corridas fallidas y pendientes"}
          </button>
        </div>
      )}
    </div>
  );
}
